import { FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge, Box, Button, Code, Flex, Text } from "@radix-ui/themes";
import styled from "@emotion/styled";
import { DateTime } from "luxon";

import { atlasRoutes } from "@/app/router/routes";
import { EventTimeline, TimelineEvent } from "@/components/timeline";
import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";

export const AssistantRunEventsPage: FC = () => {
  const navigate = useNavigate();
  const { assistantRunId } = useParams<{ assistantRunId: Uuid }>();

  if (!assistantRunId) {
    return <div>Invalid Assistant Run ID</div>;
  }

  const runQuery = useQuery({
    queryKey: ["assistant-run", assistantRunId],
    queryFn: () =>
      api.get("/internal/v1/assistant-runs/{assistant_run_id}", {
        params: { path: { assistant_run_id: assistantRunId } },
      }),
    staleTime: 30 * 1000,
  });

  const eventsQuery = useQuery({
    queryKey: ["assistant-run-events", assistantRunId],
    queryFn: () =>
      api.get("/internal/v1/assistant-runs/{assistant_run_id}/events", {
        params: {
          path: { assistant_run_id: assistantRunId },
          query: { limit: 200, offset: 0 },
        },
      }),
    staleTime: 30 * 1000,
  });

  const run = runQuery.data?.data;
  const events = eventsQuery.data?.data?.results ?? [];
  const total = eventsQuery.data?.data?.total ?? 0;
  const logfireQuery = assistantRunId
    ? encodeURIComponent(
        `attributes->>'assistant_run_id' = '${assistantRunId}'`
      ).replaceAll("%20", "+")
    : null;
  const logfireUrl = logfireQuery
    ? `https://logfire-us.pydantic.dev/mem/backend?q=${logfireQuery}&last=%226h%22`
    : null;

  const getStatusColor = (status: string): "green" | "red" | "gray" | "blue" | "orange" => {
    switch (status) {
      case "success":
        return "green";
      case "error":
        return "red";
      case "running":
        return "blue";
      case "canceled":
        return "gray";
      default:
        return "orange";
    }
  };

  const timelineEvents: TimelineEvent[] = events.map(event => {
    const eventData = event.data as { kind?: string; value?: Record<string, unknown> };
    return {
      id: event.id,
      kind: eventData?.kind ?? "UNKNOWN",
      occurred_at: event.locally_created_at,
      properties_json: eventData?.value ?? {},
    };
  });

  return (
    <Container>
      <Flex gap="3" align="center">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          ← Back
        </Button>
        {run?.session_id && (
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                atlasRoutes.assistantsViewTab({
                  params: { sessionId: run.session_id, tab: "timeline" },
                }).path
              )
            }
          >
            Back to Assistant
          </Button>
        )}
        {logfireUrl && (
          <Button asChild variant="outline">
            <a href={logfireUrl} target="_blank" rel="noreferrer">
              View Logfire →
            </a>
          </Button>
        )}
      </Flex>

      <Box mt="4">
        <Text size="5" weight="bold">
          Assistant Run Events
        </Text>
      </Box>

      <Box mt="2">
        <Flex gap="3" align="center" wrap="wrap">
          <Text size="2">
            Run: <Code>{assistantRunId}</Code>
          </Text>
          {run?.session_id && (
            <Text size="2">
              Session: <Code>{run.session_id}</Code>
            </Text>
          )}
          {run?.associated_session_run_id && (
            <Text size="2">
              Session Run: <Code>{run.associated_session_run_id}</Code>
            </Text>
          )}
          {run?.status && <Badge color={getStatusColor(run.status)}>{run.status}</Badge>}
        </Flex>
      </Box>

      {run?.started_at || run?.completed_at ? (
        <Box mt="2">
          <Flex gap="3" align="center" wrap="wrap">
            {run?.started_at && (
              <Text size="2" color="gray">
                Started: {DateTime.fromISO(run.started_at).toLocaleString(DateTime.DATETIME_SHORT)}
              </Text>
            )}
            {run?.completed_at && (
              <Text size="2" color="gray">
                Completed:{" "}
                {DateTime.fromISO(run.completed_at).toLocaleString(DateTime.DATETIME_SHORT)}
              </Text>
            )}
            {run?.duration_ms && (
              <Text size="2" color="gray">
                Duration: {run.duration_ms}ms
              </Text>
            )}
            {run?.summary && (
              <Text size="2" color="gray">
                Summary: {run.summary}
              </Text>
            )}
          </Flex>
        </Box>
      ) : null}

      {run?.description ? (
        <Box mt="2">
          <Text size="2" color="gray">
            Description: {run.description}
          </Text>
        </Box>
      ) : null}

      {run?.error_message ? (
        <Box mt="2">
          <Text size="2" color="red">
            {run.error_message}
          </Text>
        </Box>
      ) : null}

      <Box mt="6">
        <EventTimeline<TimelineEvent>
          events={timelineEvents}
          total={total}
          isLoading={eventsQuery.isLoading}
          isError={eventsQuery.isError}
          emptyMessage="No session events recorded for this run."
          sortDirection="desc"
          title="Session Events"
        />
      </Box>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;
